import React, { useState, useMemo } from 'react';
import styles from './SelectedDiagnosticsCounter.module.css';
import { Category } from '../../Utils/SelectionPane/SelectionPane';
import { ISelectedDiagnostic } from '../../../Types/Analyse/ISelectedDiagnostic';

type SelectedDiagnosticsCounterProps = {
  categories: Category<string>[];
  onDiagnosticToggle: (category: Category<string>) => void;
};

const SelectedDiagnosticsCounter = ({ categories, onDiagnosticToggle }: SelectedDiagnosticsCounterProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const selectedDiagnostics = useMemo(() => {
    const selectedItems: ISelectedDiagnostic[] = [];

    categories.forEach((category) => {
      category.data.forEach((item) => {
        if (item.selected && !item.disabled) {
          // Parse category title to extract department/group info
          const categoryTitle = category.title;
          let department = '';
          let group = '';

          if (categoryTitle.includes(' - Department')) {
            department = categoryTitle.replace(' - Department', '');
          } else if (categoryTitle.includes(' - Group')) {
            group = categoryTitle.replace(' - Group', '');
          }

          selectedItems.push({
            id: item.id,
            name: item.text,
            category: categoryTitle,
            department,
            group,
          });
        }
      });
    });

    return selectedItems;
  }, [categories]);

  const groupedDiagnostics = useMemo(() => {
    const groups: { [key: string]: typeof selectedDiagnostics } = {};
    
    selectedDiagnostics.forEach((item) => {
      const groupKey = item.department || item.group || item.category;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });

    return groups;
  }, [selectedDiagnostics]);

  const totalSelected = selectedDiagnostics.length;

  const handleDiagnosticToggle = (item: typeof selectedDiagnostics[0]) => {
    // Find the category that contains this item
    const category = categories.find(cat => cat.title === item.category);
    
    if (category) {
      // Toggle the item in the category
      const updatedData = category.data.map(dataItem => 
        dataItem.id === item.id 
          ? { ...dataItem, selected: !dataItem.selected }
          : dataItem
      );
      
      onDiagnosticToggle({ ...category, data: updatedData });
    }
  };

  if (totalSelected === 0) {
    return null;
  }

  return (
    <div className={`${styles.container} ${isExpanded ? styles.expanded : ''}`}>
      {!isExpanded ? (
        <button
          className={styles.counterButton}
          onClick={() => setIsExpanded(true)}
        >
          <span className={styles.count}>
            {totalSelected} selected
          </span>
          <span className={styles.arrow}>▲</span>
        </button>
      ) : (
        <div className={styles.expandedContent}>
          <div className={styles.header}>
            <span className={styles.title}>
              {totalSelected} selected
            </span>
            <button
              className={styles.closeButton}
              onClick={() => setIsExpanded(false)}
            >
              ×
            </button>
          </div>
          
          <div className={styles.diagnosticsList}>
            {Object.entries(groupedDiagnostics).map(([groupName, items], groupIndex) => (
              <div key={groupName}>
                {groupIndex > 0 && <hr className={styles.separator} />}
                <div className={styles.groupHeader}>{groupName}</div>
                {items.map((item, index) => (
                  <div key={`${item.name}-${index}`} className={styles.diagnosticItem}>
                    <span className={styles.diagnosticName}>{item.name}</span>
                    <button
                      onClick={() => handleDiagnosticToggle(item)}
                      className={styles.removeButton}
                      aria-label="Remove diagnostic"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectedDiagnosticsCounter;
